// Чистые хелперы для списка моделей провайдера: без React и без сети —
// сортировка/фильтрация/форматирование, которые удобно проверить тестами
// без DOM (см. models.test.ts).
import type { Translate } from '../i18n';
import type { AiModel, AiSource } from './aiApi';

/** Схлопывает подряд идущие пробелы: вставленный вручную id часто содержит
    двойные пробелы или неразрывный пробел после копипаста из веба. */
function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, ' ');
}

export interface FilterModelsOptions {
  query?: string;
  /** Оставить только модели, принимающие изображения (для источника «скриншот»). */
  visionOnly?: boolean;
}

export function filterModels(models: AiModel[], opts: FilterModelsOptions = {}): AiModel[] {
  const q = normalize(opts.query ?? '');
  return models.filter((m) => {
    if (opts.visionOnly && !m.vision) return false;
    if (!q) return true;
    return normalize(m.id).includes(q) || normalize(m.name).includes(q);
  });
}

/** Провайдер — часть id до первого '/' ('google/gemini-2.5-flash' -> 'google');
    если слэша нет, группируем модель сама с собой. */
function providerOf(model: AiModel): string {
  const i = model.id.indexOf('/');
  return i === -1 ? model.id : model.id.slice(0, i);
}

/** Сначала по провайдеру, внутри — по названию: рядом оказываются модели одной
    компании, а не вперемешку в алфавите по полному id. */
export function sortModels(models: AiModel[]): AiModel[] {
  return [...models].sort((a, b) => {
    const byProvider = providerOf(a).localeCompare(providerOf(b));
    if (byProvider !== 0) return byProvider;
    return a.name.localeCompare(b.name);
  });
}

export function modelById(models: AiModel[], id: string): AiModel | undefined {
  return models.find((m) => m.id === id);
}

/**
 * Цена за 1M токенов. Обычная форма — два знака, как у денег; но у дешёвых
 * моделей цена бывает вида $0.075, и два знака превратили бы её в «0.08», а
 * совсем мелкую — в «0.00», то есть в «бесплатно», чем она не является.
 * Поэтому для таких значений знаков берём больше.
 */
function formatUsd(value: number): string {
  const short = value.toFixed(2);
  if (value > 0 && Number(short) === 0) return String(Number(value.toPrecision(2)));
  return short;
}

export function formatPrice(model: AiModel | undefined, t: Translate): string {
  if (!model) return '';
  if (model.free) return t('admin.ai.model.free');
  if (model.promptPrice == null || model.completionPrice == null) return '';
  // Три знака, а не два: у дешёвых моделей цена за 1M токенов бывает вида
  // $0.075, и округление до центов превратило бы её в $0.08 — разница в
  // сравнении моделей между собой заметная.
  return t('admin.ai.model.price', {
    in: formatUsd(model.promptPrice),
    out: formatUsd(model.completionPrice),
  });
}

/** 128000 -> '128K', 1000000 -> '1M', 1500000 -> '1.5M' — как принято
    у провайдеров моделей, а не полное число с разрядами. */
function formatTokenCount(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${Number.isInteger(m) ? m : Math.round(m * 10) / 10}M`;
  }
  if (n >= 1_000) {
    const k = n / 1000;
    return `${Number.isInteger(k) ? k : Math.round(k * 10) / 10}K`;
  }
  return String(n);
}

export function formatContext(model: AiModel | undefined, t: Translate): string {
  if (!model || model.contextLength == null) return '';
  return t('admin.ai.model.context', { tokens: formatTokenCount(model.contextLength) });
}

/**
 * Чего задача требует от модели. Это не пожелания, а условия работы: сервер
 * шлёт всем задачам строгий `json_schema`, скриншоты уходят картинками, а
 * веб-поиск цен подключается через `tools`. Модель без нужного флага не
 * «отвечает хуже» — она возвращает ошибку провайдера, и понять почему,
 * глядя на пустой результат в интерфейсе, нельзя.
 *
 * Контекст магии — из admin.ai.req.context: в один запрос уходит вся
 * библиотека блоков.
 */
const MAGIC_MIN_CONTEXT = 128_000;

export interface SourceNeeds {
  vision: boolean;
  tools: boolean;
  minContext: number;
}

export const SOURCE_NEEDS: Record<AiSource, SourceNeeds> = {
  screenshot: { vision: true, tools: false, minContext: 0 },
  url: { vision: false, tools: false, minContext: 0 },
  text: { vision: false, tools: false, minContext: 0 },
  magic: { vision: false, tools: false, minContext: MAGIC_MIN_CONTEXT },
  prices: { vision: false, tools: true, minContext: 0 },
};

/**
 * `json` | `vision` | `tools` | `context` — задача не заработает вовсе;
 * `reasoning` — заработает, но лимит токенов надо поднять, потому что
 * размышление модели тратит тот же max_tokens, что и ответ.
 */
export type ModelIssue = 'json' | 'vision' | 'tools' | 'context' | 'reasoning';

/** Токенов ответа, ниже которых модели с неотключаемым рассуждением мало. */
export const REASONING_MIN_TOKENS = 4000;

/**
 * Чем выбранная модель не подходит задаче. Пустой массив — подходит.
 * Модель, которой нет в списке провайдера (ручной ввод, снятая с продажи),
 * проверить нечем: про неё ничего не известно, и выдумывать претензии к ней
 * хуже, чем промолчать.
 */
export function modelIssues(
  model: AiModel | undefined,
  source: AiSource,
  maxTokens: number,
): ModelIssue[] {
  if (!model) return [];
  const needs = SOURCE_NEEDS[source];
  const issues: ModelIssue[] = [];
  if (!model.json) issues.push('json');
  if (needs.vision && !model.vision) issues.push('vision');
  if (needs.tools && !model.tools) issues.push('tools');
  if (needs.minContext > 0 && model.contextLength != null && model.contextLength < needs.minContext) {
    issues.push('context');
  }
  if (model.reasoningMandatory && maxTokens < REASONING_MIN_TOKENS) issues.push('reasoning');
  return issues;
}
